/**
 * ========================================
 * usePermissionCheck Hook
 * ========================================
 * 
 * A custom hook to easily check permissions for any module and submodule.
 * This hook simplifies permission checking across all pages.
 * 
 * USAGE EXAMPLE:
 * ==============
 * 
 * import { usePermissionCheck } from '../../../../hooks/usePermissionCheck';
 * 
 * const MyPage = () => {
 *   const { canCreate, canUpdate, canDelete, canView, hasAnyPermission } = 
 *     usePermissionCheck('configuration', 'department');
 *   
 *   return (
 *     <PageTableHeader 
 *       add={canCreate}
 *       onClickAdd={handleAdd}
 *     />
 *   );
 * };
 * 
 * @param {string} module - Module name (e.g., 'configuration', 'hr', 'finance')
 * @param {string} submodule - Submodule name (e.g., 'department', 'user', 'lead')
 * @returns {Object} Permission flags object
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { shouldBypassPermissionForModule } from '../configs/menuConfig';

export const usePermissionCheck = (module, submodule) => {
  const { permissions, role: userType } = useSelector((state) => state.user);

  return useMemo(() => {
    // PRIORITY 1: If user is Super Admin (0) or Workspace Admin (1), allow all access
    // This matches the behavior in ProtectedRoute component
    if (userType === 0 || userType === 1) {
      return {
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canView: true,
        hasAnyPermission: true,
        allPermissions: [`${submodule}-view`, `${submodule}-create`, `${submodule}-update`, `${submodule}-delete`],
        
        // Helper function to check custom permission
        hasPermission: (action) => true,
        
        // Helper to check multiple permissions at once
        hasAnyOf: (...actions) => true,
        
        // Helper to check all permissions
        hasAllOf: (...actions) => true,
      };
    }

    // PRIORITY 2: Check if this module/submodule should bypass permission checks for current user type
    const shouldBypass = shouldBypassPermissionForModule(module, submodule, userType);
    
    if (shouldBypass) {
      // Return all permissions as true for bypassed modules (e.g., user type 3 accessing leads)
      return {
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canView: true,
        hasAnyPermission: true,
        allPermissions: [`${submodule}-view`, `${submodule}-create`, `${submodule}-update`, `${submodule}-delete`],
        
        // Helper function to check custom permission
        hasPermission: (action) => true,
        
        // Helper to check multiple permissions at once
        hasAnyOf: (...actions) => true,
        
        // Helper to check all permissions
        hasAllOf: (...actions) => true,
      };
    }
    
    // If no permissions loaded or no module/submodule provided
    if (!permissions || !module || !submodule) {
      return {
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canView: false,
        hasAnyPermission: false,
        allPermissions: [],
        
        // Helper function to check custom permission
        hasPermission: (action) => false,
        
        // Helper to check multiple permissions at once
        hasAnyOf: (...actions) => false,
        
        // Helper to check all permissions
        hasAllOf: (...actions) => false,
      };
    }

    // Get module permissions
    const modulePermissions = permissions[module];
    if (!modulePermissions) {
      return {
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canView: false,
        hasAnyPermission: false,
        allPermissions: [],
        
        // Helper function to check custom permission
        hasPermission: (action) => false,
        
        // Helper to check multiple permissions at once
        hasAnyOf: (...actions) => false,
        
        // Helper to check all permissions
        hasAllOf: (...actions) => false,
      };
    }

    // Get submodule permissions
    const submodulePermissions = modulePermissions[submodule] || [];

    // Create permission string template
    const permissionKey = (action) => `${submodule}-${action}`;

    // Check individual permissions
    const canCreate = submodulePermissions.includes(permissionKey('create'));
    const canUpdate = submodulePermissions.includes(permissionKey('update'));
    const canDelete = submodulePermissions.includes(permissionKey('delete'));
    const canView = submodulePermissions.includes(permissionKey('view'));

    // Check if user has any permission
    const hasAnyPermission = submodulePermissions.length > 0;

    return {
      canCreate,
      canUpdate,
      canDelete,
      canView,
      hasAnyPermission,
      allPermissions: submodulePermissions,
      
      // Helper function to check custom permission
      hasPermission: (action) => submodulePermissions.includes(permissionKey(action)),
      
      // Helper to check multiple permissions at once
      hasAnyOf: (...actions) => 
        actions.some(action => submodulePermissions.includes(permissionKey(action))),
      
      // Helper to check all permissions
      hasAllOf: (...actions) => 
        actions.every(action => submodulePermissions.includes(permissionKey(action))),
    };
  }, [permissions, module, submodule, userType]);
};

/**
 * ========================================
 * useModulePermissions Hook
 * ========================================
 * 
 * Gets all permissions for a specific module (useful for checking multiple submodules)
 * 
 * USAGE EXAMPLE:
 * ==============
 * 
 * const { getSubmodulePermissions, hasModuleAccess } = useModulePermissions('configuration');
 * 
 * const departmentPerms = getSubmodulePermissions('department');
 * const hasAccess = hasModuleAccess();
 */
export const useModulePermissions = (module) => {
  const { permissions } = useSelector((state) => state.user);

  return useMemo(() => {
    if (!permissions || !module) {
      return {
        modulePermissions: {},
        hasModuleAccess: false,
        getSubmodulePermissions: () => [],
      };
    }

    const modulePermissions = permissions[module] || {};
    const hasModuleAccess = Object.keys(modulePermissions).length > 0;

    return {
      modulePermissions,
      hasModuleAccess,
      getSubmodulePermissions: (submodule) => modulePermissions[submodule] || [],
    };
  }, [permissions, module]);
};

export default usePermissionCheck;

